import React, { useState } from "react";
import { getGpt3Suggestion } from "../services/apiService";
import { Button, Heading, Select, Text } from "@paro.io/base-ui";
import { COMPANY_SIZE_LIST, INDUSTRY_LIST } from "../data/appConstants";

const escapeJSON = (str) => str.replace(/\\/g,'\\');

export default function Gpt3Component({ defaultValues, notes, setValue }) {
  return (
    <>
      <Heading size="h5">Optimizations</Heading>
      <div className="mt-1">
        <Gpt3TitleComponent
          proposalId={defaultValues.id}
          notes={notes}
          numberOfEmployees={
            defaultValues.client?.firmographics?.numberOfEmployees
          }
          industry={defaultValues.client?.firmographics?.industry}
          setTitleValue={(v) => setValue("name", v)}
          originalTitle={defaultValues.name}
        />
        <Gpt3DescComponent
          proposalId={defaultValues.id}
          notes={notes}
          setDescValue={(v) => setValue("description", v)}
          originalDescription={defaultValues.description}
        />
      </div>
    </>
  );
}

function Gpt3TitleComponent({
  proposalId,
  notes,
  numberOfEmployees,
  industry,
  setTitleValue,
  originalTitle,
}) {
  const companySizeOptions = COMPANY_SIZE_LIST.map((c) => ({
    label: c,
    value: c,
  }));

  const industryOptionsFirmographics = INDUSTRY_LIST.map((i) => ({
    label: i,
    value: i,
  }));

  const [companySizeValue, setCompanySizeValue] = useState(numberOfEmployees);
  const [industryValue, setIndustryValue] = useState(industry);

  const [generatingTitle, setGeneratingTitle] = useState(false);

  const [optimizeTitle, setOptimizeTitle] = useState(false);

  const [titleError, setTitleError] = useState(false);
  const [titleGenerated, setTitleGenerated] = useState(null);
  const [useTitle, setUseTitle] = useState(false);
  const getTitleSuggestion = async (data) => {
    const res = await getGpt3Suggestion(data);
    const suggestion = res.data.data.getGpt3Suggestion;

    if (!suggestion) {
      return setTitleError("Failed to fetch optimized title");
    }
    if (
      suggestion.Output ===
      "Insufficient Information to generate Proposal Card Title"
    ) {
      return setTitleError(
        "Insufficient Information to generate Proposal Card Title. Please update Call Notes."
      );
    }
    setTitleGenerated(suggestion?.Output);
  };

  return (
    <>
      <Text size="lg">Proposal Title</Text>

      {!optimizeTitle && (
        <div id="mm-gpt-OptimizeTitle">
          <Button
            label="Optimize Title"
            color="success"
            onClick={() => setOptimizeTitle(true)}
          ></Button>
        </div>
      )}

      {optimizeTitle && !titleGenerated && (
        <div className="mb-3">
          <span id="mm-gpt-companySize">
            <Select
              label="Company Size*"
              options={companySizeOptions}
              value={companySizeValue}
              onChange={(event) => setCompanySizeValue(event.target.value)}
              className="mb-3"
            />
          </span>
          <span id="mm-gpt-companySize">
            <Select
              label="Main Industry*"
              options={industryOptionsFirmographics}
              value={industryValue}
              onChange={(event) => setIndustryValue(event.target.value)}
              className="mb-3"
            />
          </span>

          {titleError && !generatingTitle && (
            <>
              <Text className="bg-gray-100 p-2 mb-3 rounded border-2">
                {titleError}
              </Text>
            </>
          )}
          {companySizeValue && industryValue ? (
            <span id="mm-gpt-generatingTitle">
              <Button
                label="Generate"
                color="success"
                isLoading={generatingTitle}
                onClick={async () => {
                  setGeneratingTitle(true);
                  await getTitleSuggestion({
                    Indicator: "TITLE_GENERATION",
                    Segment: companySizeValue,
                    Industry: industryValue,
                    Notes: escapeJSON(notes),
                    proposalId: proposalId,
                    title: originalTitle,
                  });
                  setGeneratingTitle(false);
                }}
              />
            </span>
          ) : (
            <span id="mm-gpt-generateTitle">
              <Button label="Generate" color="success" disabled />
            </span>
          )}
        </div>
      )}

      {titleGenerated && !useTitle && (
        <>
          <Text className="bg-primary-lighter p-2 mb-3">{titleGenerated}</Text>
          <span id="mm-gpt-useTitle">
            <Button
              label="Use Title"
              color="success"
              onClick={() => {
                setTitleValue(titleGenerated);
                setUseTitle(true);
              }}
            />
          </span>
        </>
      )}

      {titleGenerated && useTitle && (
        <div id="mm--gpt-resetTitle">
          <Button
            label="Reset to Original"
            onClick={() => {
              setTitleValue(originalTitle);
              setUseTitle(false);
            }}
          />
        </div>
      )}
    </>
  );
}

function Gpt3DescComponent({
  proposalId,
  notes,
  setDescValue,
  originalDescription,
}) {
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [descError, setDescError] = useState(false);

  const [descGenerated, setDescGenerated] = useState(null);
  const [useDesc, setUseDesc] = useState(false);

  const getDescSuggestion = async (data) => {
    const res = await getGpt3Suggestion(data);
    const suggestion = res.data.data.getGpt3Suggestion;

    if (!suggestion) {
      return setDescError("Failed to fetch optimized description");
    }
    if (
      suggestion.Output === "Insufficient Information to generate Proposal Card"
    ) {
      return setDescError(
        "Insufficient Information to generate Proposal Card Description. Please update Call Notes."
      );
    }
    setDescGenerated(suggestion?.Output);
  };

  return (
    <>
      <Text size="lg">Proposal Description</Text>

      {descError && !generatingDescription && (
        <>
          <Text className="bg-gray-100 p-2 mb-3 rounded border-2">
            {descError}
          </Text>
        </>
      )}
      {!descGenerated && (
        <div id="mm-gpt-generatingDescription">
          <Button
            label="Optimize Description"
            color="success"
            isLoading={generatingDescription}
            onClick={async () => {
              setGeneratingDescription(true);
              await getDescSuggestion({
                proposalId: proposalId,
                Indicator: "PROPOSAL_GENERATION",
                Segment: "",
                Notes: escapeJSON(notes),
                Industry: "",
                description: originalDescription,
              });
              setGeneratingDescription(false);
              // setOptimizeDesc(true);
            }}
          />
        </div>
      )}

      {descGenerated && !useDesc && (
        <>
          <Text className="bg-primary-lighter p-2 mb-3">{descGenerated}</Text>
          <span id="mm-gpt-useDescription">
            <Button
              label="Use Description"
              color="success"
              onClick={() => {
                setDescValue(descGenerated);
                setUseDesc(true);
              }}
            />
          </span>
        </>
      )}

      {descGenerated && useDesc && (
        <div id="mm-gpt-resetTitle">
          <Button
            label="Reset to Original"
            onClick={() => {
              setDescValue(originalDescription);
              setUseDesc(false);
            }}
          />
        </div>
      )}
    </>
  );
}
